import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Page } from '../../components/shell/Page';
import ExplorerLayout from '../../components/shell/ExplorerTree';
import useExamGroupTree from '../../hooks/useExamGroupTree';
import { useCategoryManagerPane } from '../../components/categories/CategoryManager';
import SplitLayout from '../../components/shell/SidePane';
import DataGrid, { StatusBadge } from '../../components/datagrid/DataGrid';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { toast } from 'sonner';
import {
  Plus,
  Pencil,
  Trash,
  ChartBar,
  Copy,
  Eye,
  FolderPlus,
} from '@phosphor-icons/react';

export default function ExamList() {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [exams, setExams] = useState([]);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCourse, setSelectedCourse] = useState('');
  // The group picked in the explorer. Selecting a group shows everything under it,
  // sub-groups included — that is what makes "10th Class" useful as a heading.
  const [treeKey, setTreeKey] = useState(null);
  const groupTree = useExamGroupTree({ api, exams });
  const groupMgr = useCategoryManagerPane({ api, onChange: () => { groupTree.reload(); fetchData(); } });

  const fetchData = async () => {
    try {
      const [examsRes, coursesRes] = await Promise.all([
        api.get(`/exams${selectedCourse ? `?course_id=${selectedCourse}` : ''}`),
        api.get('/courses')
      ]);
      setExams(examsRes.data);
      setCourses(coursesRes.data);
    } catch (error) {
      toast.error('Failed to load exams');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [selectedCourse]);

  const handleDelete = async (examId) => {
    if (!window.confirm('Are you sure you want to delete this exam? All attempts will also be deleted.')) return;

    try {
      await api.delete(`/exams/${examId}`);
      toast.success('Exam deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete exam');
    }
  };

  const viewReport = (examId) => {
    navigate(`/instructor/exams/${examId}/report`);
  };

  const duplicateExam = async (exam) => {
    try {
      const res = await api.get(`/exams/${exam.id}`);
      const examData = res.data;

      const newExam = {
        title: `${examData.title} (Copy)`,
        description: examData.description,
        course_id: examData.course_id,
        assessment_type: examData.assessment_type,
        settings: examData.settings,
        questions: examData.questions,
        random_selection: examData.random_selection,
        random_count: examData.random_count,
        random_filters: examData.random_filters,
        instructions: examData.instructions,
        is_published: false
      };

      await api.post('/exams', newExam);
      toast.success('Exam duplicated');
      fetchData();
    } catch (error) {
      toast.error('Failed to duplicate exam');
    }
  };

  const openExam = (exam) => navigate(`/instructor/exams/${exam.id}`);
  const courseTitle = (exam) => courses.find((c) => c.id === exam.course_id)?.title || '';

  // What the explorer selection narrows the grid to.
  const treeEntry = treeKey ? groupTree.byKey(treeKey) : null;
  const visibleExams = React.useMemo(() => {
    if (!treeEntry) return exams;
    if (treeEntry.kind === 'exam') return exams.filter((e) => e.id === treeEntry.node.data.id);
    if (treeEntry.kind === 'ungrouped') return exams.filter((e) => !(e.category_id || e.bank_category_id));
    const ids = new Set(treeEntry.descendants.filter((d) => d.kind === 'exam').map((d) => d.data.id));
    return exams.filter((e) => ids.has(e.id));
  }, [exams, treeEntry]);
  const groupPath = (e) => groupTree.groups.find((g) => g.id === (e.category_id || e.bank_category_id))?.path || '';

  if (loading) {
    return (
      <DashboardLayout>
        <div className="p-8 flex items-center justify-center min-h-[60vh]">
          <div className="animate-pulse">Loading exams...</div>
        </div>
      </DashboardLayout>
    );
  }

  const explorer = {
    title: 'Exam groups',
    nodes: groupTree.nodes,
    selectedKey: treeKey,
    onSelect: (n) => setTreeKey(n && n.key !== treeKey ? n.key : null),
    loading: groupTree.loading,
    storageKey: 'exams',
    footer: (
      <Button variant="ghost" size="sm" className="w-full justify-start h-8 text-[12px]"
        onClick={() => groupMgr.open()} data-testid="manage-groups-btn">
        <FolderPlus className="w-4 h-4" /> Manage groups
      </Button>
    ),
  };

  return (
    <DashboardLayout>
      <SplitLayout side={groupMgr.side}>
      <ExplorerLayout explorer={explorer}>
      <Page data-testid="exam-list-page">
        <DataGrid
          title={treeEntry && treeEntry.kind === 'group' ? `Exams · ${treeEntry.node.data.path}` : 'Exams'}
          rows={visibleExams}
          loading={loading}
          onRefresh={fetchData}
          searchKeys={['description']}
          columns={[
            { key: 'title', header: 'Title', render: (e) => <span className="font-medium">{e.title}</span> },
            { key: 'assessment_type', header: 'Type', width: 120, accessor: (e) => e.assessment_type || '', render: (e) => (e.assessment_type ? <StatusBadge value={e.assessment_type} tone="neutral" /> : '—') },
            { key: 'group', header: 'Group', width: 200, accessor: groupPath,
              render: (e) => (groupPath(e)
                ? <span className="text-xs truncate block" title={groupPath(e)}>{groupPath(e)}</span>
                : <span className="text-muted-foreground">—</span>) },
            { key: 'course', header: 'Course', accessor: courseTitle, render: (e) => courseTitle(e) || '—', className: 'text-muted-foreground' },
            { key: 'question_count', header: 'Questions', width: 110, align: 'right', accessor: (e) => e.question_count ?? 0 },
            { key: 'bank', header: 'Question bank', width: 180, accessor: (e) => (e.in_question_bank ? e.bank_category_path || 'Listed' : ''), render: (e) => (e.in_question_bank ? <span className="text-xs truncate block" title={e.bank_category_path || ''}>{e.bank_category_path || 'Listed'}</span> : <span className="text-muted-foreground">—</span>) },
            { key: 'total_points', header: 'Points', width: 100, align: 'right', accessor: (e) => e.total_points ?? 0 },
            { key: 'time_limit', header: 'Time limit', width: 110, accessor: (e) => e.settings?.time_limit_minutes ?? null, render: (e) => (e.settings?.time_limit_minutes ? `${e.settings.time_limit_minutes} min` : '—') },
            { key: 'attempt_count', header: 'Attempts', width: 100, align: 'right', accessor: (e) => e.attempt_count ?? 0 },
            { key: 'is_published', header: 'Status', width: 110, accessor: (e) => (e.is_published ? 'published' : 'draft'), render: (e) => <StatusBadge value={e.is_published ? 'published' : 'draft'} /> },
            { key: 'updated_at', header: 'Updated', width: 120, accessor: (e) => e.updated_at || e.created_at || null, render: (e) => { const d = e.updated_at || e.created_at; return d ? new Date(d).toLocaleDateString() : '—'; } },
          ]}
          rowTone={(e) => (e.is_published ? 'success' : 'muted')}
          onRowDoubleClick={openExam}
          advancedSearch={(
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">Course</Label>
                <Select value={selectedCourse || "all"} onValueChange={(v) => setSelectedCourse(v === "all" ? "" : v)}>
                  <SelectTrigger className="h-9" data-testid="course-filter">
                    <SelectValue placeholder="All Courses" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Courses</SelectItem>
                    {courses.map(c => (
                      <SelectItem key={c.id} value={c.id}>{c.title}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
          activeFilterCount={selectedCourse ? 1 : 0}
          primaryActions={(
            <Button size="sm" className="h-9" onClick={() => navigate('/instructor/exams/new')} data-testid="create-exam-btn">
              <Plus className="w-4 h-4" /> New exam
            </Button>
          )}
          selectionActions={[
            { key: 'preview', label: 'Preview', icon: Eye, onClick: ([e]) => navigate(`/instructor/exams/${e.id}/preview`), testid: 'preview-exam' },
            { key: 'edit', label: 'Edit', icon: Pencil, onClick: ([e]) => openExam(e) },
            { key: 'report', label: 'Report', icon: ChartBar, onClick: ([e]) => viewReport(e.id) },
            { key: 'duplicate', label: 'Duplicate', icon: Copy, onClick: ([e]) => duplicateExam(e) },
            { key: 'delete', label: 'Delete', icon: Trash, variant: 'destructive', onClick: ([e]) => handleDelete(e.id) },
          ]}
          emptyMessage={treeEntry
            ? 'Nothing in this group yet — create an exam and pick this group for it.'
            : 'No exams yet — create your first exam to assess your learners'}
        />
      </Page>
      </ExplorerLayout>
      </SplitLayout>
    </DashboardLayout>
  );
}
